class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const line = parseInt(this.dataset.index, 10);
      
      // Find the input and trigger a change event.
      // This centralizes all update logic in the CartItems.onChange method.
      const quantityInput = cartItems.querySelector(`#Drawer-quantity-${line}`) || cartItems.querySelector(`#Quantity-${line}`);
      if (quantityInput) {
        quantityInput.value = 0;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.isUpdating = false; // Flag to prevent concurrent updates

    // Debounce the main onChange handler to group rapid clicks
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 400);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    // Prevent concurrent updates
    if (this.isUpdating) {
      return;
    }
    
    // Determine which line item triggered the change for the loading spinner
    const changedInput = event.target;
    const line = changedInput.hasAttribute('data-index') ? parseInt(changedInput.dataset.index, 10) : null;

    this.isUpdating = true;
    if (line) {
      this.enableLoading(line);
    }
    this.clearAllErrors();

    // Build the payload for /cart/update.js using item keys for reliability
    const updates = {};
    const cartItemElements = this.querySelectorAll('.cart-item');
    
    cartItemElements.forEach(itemEl => {
      // The item key is stored in a span used by the MakeBeCool app
      const keyElement = itemEl.querySelector('[data-item-key]');
      const quantityElement = itemEl.querySelector('input[name="updates[]"]');
      
      if (keyElement && keyElement.dataset.itemKey && quantityElement) {
        const key = keyElement.dataset.itemKey;
        const quantity = parseInt(quantityElement.value, 10);
        updates[key] = quantity;
      }
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    
    fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        if (!response.ok) {
          return response.text().then(text => {
            console.error('[Cart] API error:', response.status, text);
            throw new Error(`Cart update failed: ${response.status}`);
          });
        }
        // Use .json() directly as /cart/update.js returns a cart object
        return response.json(); 
      })
      .then((state) => {
        // --- SIMPLIFIED RENDER FLOW ---
        // With /cart/update.js, the gift should no longer be removed by the server.
        // All complex logic for detecting and re-adding the gift is no longer needed.
        const parsedState = state;

        // Safety check for sections (if the response for some reason is just the cart object)
        if (!parsedState.sections) {
          console.warn('[Cart] No sections in response, skipping section updates. Will refresh page to be safe.');
          window.location.reload(); // Refresh as a fallback
          return;
        }

        if (window.cartEnhancements && typeof window.cartEnhancements.updateState === 'function') {
          window.cartEnhancements.updateState(parsedState);
        }
        
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));

        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = line ? (document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`)) : null;
        if (lineItem && lineItem.querySelector(`[name="updates[]"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="updates[]"]`)) : lineItem.querySelector(`[name="updates[]"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        
        this.disableLoading();
        this.isUpdating = false;

        // The Makebecool client-side call might still be needed for its own UI updates.
        if (window.MAKEBECOOL_BUNDLES && typeof window.MAKEBECOOL_BUNDLES.updateCart === 'function') {
            window.MAKEBECOOL_BUNDLES.updateCart();
        }
        
        return parsedState;

      }).catch((error) => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        this.disableLoading();
        this.isUpdating = false;
        throw error;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ].filter(section => section.section); // Filter out sections that don't exist
  }

  updateLiveRegions(line, itemCount) {
    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }
  
  clearAllErrors() {
    const errorElements = document.querySelectorAll('.cart-item__error-text');
    errorElements.forEach(el => el.innerHTML = '');
    const cartErrors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if(cartErrors) cartErrors.textContent = '';
  }
  
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if(mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if(mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
      }, 300))
    }
  });
};
